import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faTimes, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const UnassignedClientAlertBanner = ({ ticket, isAssigned = true }) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [isDismissed, setIsDismissed] = useState(false);

    const ticketId = ticket?.idTicket || ticket?.id;
    const hasNoClient = Boolean(isAssigned && ticket && !ticket.idClienteAkasia);

    // Resetear el estado dismissed si cambia de ticket
    useEffect(() => {
        setIsDismissed(false);
    }, [ticketId]);

    // Escuchar evento para reabrir la alerta si el asesor le da clic al badge en el navbar superior
    useEffect(() => {
        const handleReopen = () => {
            setIsDismissed(false);
        };
        window.addEventListener('akasia:support:reopen_unassigned_client_alert', handleReopen);
        return () => {
            window.removeEventListener('akasia:support:reopen_unassigned_client_alert', handleReopen);
        };
    }, []);

    // Notificar al Navbar superior y móvil el estado de la alerta (para mostrar u ocultar el badge)
    useEffect(() => {
        window.dispatchEvent(
            new CustomEvent('akasia:support:unassigned_client_alert_state', {
                detail: {
                    isDismissed,
                    hasNoClient
                }
            })
        );
    }, [isDismissed, hasNoClient]);

    if (isDismissed || !hasNoClient) return null;

    const handleDismiss = () => {
        setIsDismissed(true);
    };

    const handleOpenAssignModal = () => {
        window.dispatchEvent(new CustomEvent('akasia:support:open_assign_client'));
    };

    return (
        <div className="flex-shrink-0 px-2 px-sm-3 pt-2 pb-1">
            <div
                className="position-relative d-flex align-items-start gap-2 gap-sm-3 p-2 p-sm-3 rounded-3"
                style={{
                    backgroundColor: isDark ? 'rgba(245, 158, 11, 0.14)' : '#fffbeb',
                    border: isDark ? '1px solid rgba(245, 158, 11, 0.45)' : '1px solid #fcd34d',
                    color: isDark ? '#fde68a' : '#92400e'
                }}
            >
                {/* Icono de advertencia ámbar parpadeante */}
                <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                        color: '#f59e0b',
                        marginTop: '2px',
                        animation: 'blinkAmber 1s infinite alternate'
                    }}
                >
                    <style>{`
                        @keyframes blinkAmber {
                            0% { opacity: 1; }
                            100% { opacity: 0.3; }
                        }
                    `}</style>
                    <FontAwesomeIcon icon={faExclamationCircle} className="fs-5 fs-sm-3" />
                </div>

                {/* Contenido central: En escritorio se distribuye en horizontal */}
                <div className="flex-grow-1 d-flex flex-column flex-md-row align-items-md-center justify-content-md-between gap-1.5 gap-md-3 pe-4 pe-md-5">
                    {/* Fila izquierda: Badge + Título + Mensaje conciso */}
                    <div className="d-flex flex-column justify-content-center">
                    <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <span
                            className="badge text-white px-2 py-0.5 text-uppercase"
                                style={{
                                backgroundColor: isDark ? '#d97706' : '#d97706',
                                fontSize: '0.68rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em'
                                }}
                            >
                                ATENCIÓN AGENTE
                            </span>
                            <span
                                className="fw-bold"
                                style={{
                                fontSize: '0.82rem',
                                    letterSpacing: '0.02em',
                                    color: isDark ? '#fbbf24' : '#b45309'
                                }}
                            >
                                TICKET SIN CLIENTE ASIGNADO
                            </span>
                        </div>

                    {/* Mensaje descriptivo */}
                        <p
                            className="mb-0 mt-0.5 d-none d-sm-block"
                            style={{
                            fontSize: '0.78rem',
                            lineHeight: 1.35,
                                color: isDark ? '#fde68a' : '#78350f'
                            }}
                        >
                            Este número de WhatsApp no está asignado a ningún cliente en Akasia. Asigna este ticket antes de brindar soporte.
                        </p>
                    </div>

                    {/* Lado derecho: Botón directo de acción para asignar cliente */}
                    <div className="flex-shrink-0 d-flex align-items-center">
                        <button
                            type="button"
                            onClick={handleOpenAssignModal}
                            className="btn btn-sm py-1 px-2.5 d-inline-flex align-items-center gap-1.5 fw-bold shadow-none text-nowrap"
                            style={{
                                backgroundColor: isDark ? '#d97706' : '#f59e0b',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.72rem',
                                cursor: 'pointer'
                            }}
                            title="Abrir buscador para asignar este ticket a un cliente"
                        >
                            <FontAwesomeIcon icon={faUserPlus} style={{ fontSize: '0.70rem' }} />
                            <span>Asignar a un Cliente</span>
                        </button>
                    </div>
                </div>

                {/* Botón de cierre de alerta (✕) */}
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="position-absolute top-50 translate-middle-y end-0 me-2 btn btn-link p-1 text-decoration-none shadow-none"
                    style={{
                        color: isDark ? '#fde68a' : '#b45309',
                        fontSize: '0.80rem',
                        lineHeight: 1,
                        cursor: 'pointer'
                    }}
                    title="Ocultar alerta (se reflejará en la barra superior)"
                    aria-label="Cerrar alerta"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

UnassignedClientAlertBanner.propTypes = {
    ticket: PropTypes.object,
    isAssigned: PropTypes.bool
};

export default UnassignedClientAlertBanner;
